import type { ReactNode } from 'react'
import { AlertErrorCircle, InfoCircle, WarningTriangle } from '@/assets/icons'

export type InlineMessageVariant = 'error' | 'neutral' | 'warning' | 'info'

export type InlineMessageProps = {
  /** @default 'neutral' */
  variant?: InlineMessageVariant
  children: ReactNode
}

/**
 * Compact inline status text.
 *
 * - **neutral** (default) — low-emphasis note
 * - **error** — validation / failure
 * - **warning** — caution without hard error
 */
export function InlineMessage({ variant = 'neutral', children }: InlineMessageProps) {
  if (variant === 'error') {
    return (
      <div
        data-ds-component="InlineMessage"
        data-ds-variant={variant}
        className="flex items-center gap-1 overflow-hidden rounded bg-red-50 px-2 py-2 text-xs font-medium leading-4 text-red-800"
        role="alert"
      >
        <AlertErrorCircle className="h-3.5 w-3.5 shrink-0 text-red-800" aria-hidden />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    )
  }

  if (variant === 'info') {
    return (
      <div
        data-ds-component="InlineMessage"
        data-ds-variant={variant}
        className="flex items-center gap-1 overflow-hidden rounded bg-brand-50 px-2 py-2 text-xs font-medium leading-4 text-brand-700"
        role="status"
      >
        <InfoCircle className="h-3.5 w-3.5 shrink-0 text-brand-700" aria-hidden />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    )
  }

  if (variant === 'warning') {
    return (
      <div
        data-ds-component="InlineMessage"
        data-ds-variant={variant}
        className="flex items-start gap-1 overflow-hidden rounded border border-brand-neutral-200 bg-yellow-50 px-2 py-2 text-xs font-medium leading-4 text-yellow-800"
        role="status"
      >
        <WarningTriangle className="h-3.5 w-3.5 shrink-0 text-yellow-800" aria-hidden />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    )
  }

  return (
    <div
      data-ds-component="InlineMessage"
      data-ds-variant={variant}
      className="flex items-center overflow-hidden rounded bg-brand-neutral-100 px-3 py-2 text-xs font-medium leading-4 text-brand-neutral-600"
      role="status"
    >
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}
